import React from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { NIA_LOGO_URL } from "@/lib/api";
import {
  Building2,
  Users,
  Settings,
  FileText,
  LogOut,
  MessagesSquare,
  UserCog,
  Eye,
  BarChart3,
  Package,
} from "lucide-react";
import { Button } from "@/components/ui/button";

export function DashboardShell({ nav, title, extraNav }) {
  const { user, logout } = useAuth();

  return (
    <div className="min-h-screen bg-slate-50">
      <aside className="w-64 fixed left-0 top-0 h-screen border-r border-slate-200 bg-white flex flex-col z-40">
        <div className="px-5 py-6 border-b border-slate-100 flex items-center gap-3">
          <img src={NIA_LOGO_URL} alt="NIA" className="h-10 w-10 object-contain" />
          <div>
            <p className="font-[Outfit] font-semibold text-slate-900 leading-tight">NIA</p>
            <p className="text-[11px] text-slate-500 leading-tight">Chatbot Institucional</p>
          </div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-400 px-3 pt-2 pb-1">
            {title}
          </p>
          {nav.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              data-testid={item.testId}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive
                    ? "bg-[#e6f0fa] text-[#1e5a8e]"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              {item.icon}
              <span>{item.label}</span>
            </NavLink>
          ))}
          {extraNav}
        </nav>

        <div className="px-3 py-4 border-t border-slate-100">
          <div className="flex items-center gap-3 px-3 py-2 mb-2">
            <div className="w-8 h-8 rounded-full bg-[#1e5a8e] text-white flex items-center justify-center text-sm font-semibold">
              {(user?.name?.[0] || user?.email?.[0] || "?").toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="text-sm font-medium text-slate-900 truncate">{user?.name || user?.email}</p>
              <p className="text-[11px] text-slate-500 truncate">
                {user?.role === "super_admin" ? "Super Admin" : "Coordenador"}
              </p>
            </div>
          </div>
          <Button
            data-testid="logout-btn"
            variant="outline"
            size="sm"
            onClick={logout}
            className="w-full justify-start text-slate-600"
          >
            <LogOut className="w-4 h-4 mr-2" /> Sair
          </Button>
        </div>
      </aside>

      <main className="ml-64 p-8 min-h-screen">
        <Outlet />
      </main>
    </div>
  );
}

export function AdminLayout() {
  const nav = [
    { to: "/admin/sectors", label: "Setores", icon: <Building2 className="w-4 h-4" />, testId: "nav-sectors" },
    { to: "/admin/users", label: "Usuários", icon: <Users className="w-4 h-4" />, testId: "nav-users" },
    { to: "/admin/documents", label: "Documentos", icon: <FileText className="w-4 h-4" />, testId: "nav-documents" },
    { to: "/admin/analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" />, testId: "nav-analytics" },
    { to: "/admin/plugin", label: "Plugin WordPress", icon: <Package className="w-4 h-4" />, testId: "nav-plugin" },
    { to: "/admin/settings", label: "Configurações", icon: <Settings className="w-4 h-4" />, testId: "nav-settings" },
  ];
  return <DashboardShell nav={nav} title="Super Admin" />;
}

export function CoordinatorLayout() {
  const nav = [
    { to: "/coordinator/documents", label: "Documentos", icon: <FileText className="w-4 h-4" />, testId: "nav-docs" },
    { to: "/coordinator/users", label: "Coordenadores", icon: <UserCog className="w-4 h-4" />, testId: "nav-users" },
    { to: "/coordinator/analytics", label: "Analytics", icon: <BarChart3 className="w-4 h-4" />, testId: "nav-analytics" },
    { to: "/coordinator/preview", label: "Preview do chat", icon: <Eye className="w-4 h-4" />, testId: "nav-preview" },
  ];
  return <DashboardShell nav={nav} title="Coordenador" />;
}
